/**
 * The guest voice / call-ordering switch (configs/ai/customer-voice.json).
 *
 * Deliberately NOT part of `configs/ai/owner-ai.json`. Owner AI and guest voice
 * are switched off for different reasons, and one switch per reason is the whole
 * point of a config switch: if voice were governed by the owner switch, then
 * turning owner AI back on would silently resurrect the microphone.
 *
 * Edit the JSON, never this wrapper (AGENTS.md config rules).
 */

import customerVoiceConfig from '@/configs/ai/customer-voice.json';

export interface CustomerVoiceConfig {
  /** Master switch. false = the guest voice routes refuse. */
  enabled: boolean;
  status: string;
  switchedOffAt?: string;
  rationale: string[];
  gatedRoutes: string[];
  gatedLibraries: string[];
  uiEntryPoints: string[];
  uiEnforcement: string;
  restoreNote?: string;
  knownDependencies?: string[];
}

const raw = customerVoiceConfig as unknown as Record<string, unknown>;

function readStringArray(key: string): string[] {
  const value = raw[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Defaults to the OFF state, fail-closed, for the same reason as
 * `ownerAiEnabled()`: a config that fails to load must not be the reason a guest
 * ends up on a live microphone path that was meant to be closed.
 */
export const customerVoice: CustomerVoiceConfig = {
  enabled: raw.enabled === true,
  status: typeof raw.status === 'string' ? raw.status : 'coming_soon',
  switchedOffAt: typeof raw.switchedOffAt === 'string' ? raw.switchedOffAt : undefined,
  rationale: readStringArray('rationale'),
  gatedRoutes: readStringArray('gatedRoutes'),
  gatedLibraries: readStringArray('gatedLibraries'),
  uiEntryPoints: readStringArray('uiEntryPoints'),
  uiEnforcement: typeof raw.uiEnforcement === 'string' ? raw.uiEnforcement : '',
  restoreNote: typeof raw.restoreNote === 'string' ? raw.restoreNote : undefined,
  knownDependencies: readStringArray('knownDependencies'),
};

/** Is guest voice ordering on? */
export function customerVoiceEnabled(): boolean {
  return customerVoice.enabled;
}

/** Why voice is refused, for the 403 body and for logs. */
export function customerVoiceDisabledReason(): string {
  return 'Voice ordering is switched off. Text chat is available and covers the same menu, tools and payment flow.';
}
